// Tab "Comisión": lo que el motor devengó por esta venta y qué regla pagó cada pieza.
import { useTranslation } from 'react-i18next';
import { CommissionAccrualStatusEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionAccrualStatusEnum';
import type { BackofficeSale } from '@/api/services/retail-wizard/contract';
import { ApiError } from '@/api/errors';
import { Badge, Button, Loading } from '@/kit';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { useAccrualBySale } from '@/domains/retail/admin/comisiones/api/commissionQueries';
import { weekLabel } from '@/domains/retail/admin/comisiones/lib/payoutPeriods';
import { PendingDetailRow } from '@/domains/sales/portal/mysales/components/SaleDetailSections';
import { accrualAmountCents, cutStatusChip } from '../../lib/ventasMetrics';
import { InfoRow, TAB_BODY, TabNote, TabSection, dash } from './VentaTab';

/** El motor no fecha el pago: el archivo del corte va a la nómina de LUGA, no lo dispersa Fiado. */
export const PAYMENT_DATE_NO_SOURCE_NOTE =
    'El pago lo hace la nómina de LUGA con el archivo del corte; el sistema no guarda su fecha.';

interface AppliedRule {
    name: string;
    count: number;
    /** Monto · SKU · posición de cada pieza que pagó la regla. */
    detail: string;
}

/** Una fila por regla: la que pagó varias piezas va una vez con su cuenta. */
function groupRules(
    bonuses: { name: string; amountCents: number; sku: string; position: number }[],
): AppliedRule[] {
    const byName = new Map<string, AppliedRule>();
    for (const bonus of bonuses) {
        const piece = `${formatMoneyCents(bonus.amountCents)} · ${bonus.sku} · #${String(bonus.position)}`;
        const rule = byName.get(bonus.name);
        if (rule) {
            rule.count += 1;
            rule.detail = `${rule.detail}\n${piece}`;
        } else {
            byName.set(bonus.name, { name: bonus.name, count: 1, detail: piece });
        }
    }
    return [...byName.values()];
}

export function ComisionTab({ sale }: { sale: BackofficeSale }) {
    const { t } = useTranslation('ventas');
    const sellerId = sale.sellerRetailUserId ?? null;
    const query = useAccrualBySale(sale.saleId, sellerId, sale.soldAt);
    const accrual = query.data?.accrual;

    const seller = (
        <TabSection title={t('comision.delVendedor', { defaultValue: 'Comisión del vendedor' })}>
            <InfoRow
                label={t('detalle.vendedor', { defaultValue: 'Vendedor' })}
                value={dash(sale.sellerName)}
            />
        </TabSection>
    );

    // El devengo se guarda por vendedor: sin él no hay a quién preguntarle.
    if (sellerId === null) {
        return (
            <div className={TAB_BODY}>
                {seller}
                <TabNote variant="warn">
                    {t('comision.sinVendedor', {
                        defaultValue:
                            'No se puede identificar al vendedor de esta venta, así que no se puede consultar su comisión.',
                    })}
                </TabNote>
            </div>
        );
    }

    if (query.isLoading) {
        return (
            <div className={TAB_BODY}>
                {seller}
                <div role="status" aria-busy="true">
                    <Loading
                        size="sm"
                        text={t('comision.cargando', { defaultValue: 'Consultando la comisión…' })}
                    />
                </div>
            </div>
        );
    }

    if (query.isError || !accrual) {
        const status = query.error instanceof ApiError ? query.error.httpStatus : null;
        if (status === 403) {
            return (
                <div className={TAB_BODY}>
                    {seller}
                    <TabNote variant="warn">
                        {t('comision.sinPermiso', {
                            defaultValue: 'No tienes permiso para ver las comisiones.',
                        })}
                    </TabNote>
                </div>
            );
        }
        // Un 404 no dice por qué: la venta puede ser anterior al motor o seguir en cola.
        const message =
            status === 404
                ? t('comision.sinDevengo', {
                      defaultValue:
                          'Aún no hay comisión registrada para esta venta. Si es reciente, puede estar en proceso.',
                  })
                : t('comision.errorLectura', {
                      defaultValue: 'No se pudo leer la comisión de esta venta.',
                  });
        return (
            <div className={TAB_BODY}>
                {seller}
                <TabNote variant="warn">{message}</TabNote>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    isLoading={query.isFetching}
                    onClick={() => {
                        void query.refetch();
                    }}
                >
                    {t('comision.reintentar', { defaultValue: 'Volver a consultar' })}
                </Button>
            </div>
        );
    }

    const reversed = accrual.status === CommissionAccrualStatusEnum.REVERSED;
    const amount = formatMoneyCents(accrualAmountCents(accrual));
    const rules = groupRules(accrual.appliedBonuses ?? []);
    const cut = cutStatusChip(accrual.status);
    return (
        <div className={TAB_BODY}>
            <TabSection
                title={t('comision.delVendedor', { defaultValue: 'Comisión del vendedor' })}
            >
                <InfoRow
                    label={t('detalle.vendedor', { defaultValue: 'Vendedor' })}
                    value={dash(sale.sellerName)}
                />
                <InfoRow
                    label={t('comision.montoComision', { defaultValue: 'Monto comisión' })}
                    value={
                        <span className="text-[14px] font-semibold tabular-nums text-success">
                            {reversed ? <s className="text-muted-foreground">{amount}</s> : amount}
                        </span>
                    }
                />
                <InfoRow
                    label={t('comision.regla', { defaultValue: 'Regla aplicada' })}
                    value={
                        rules.length === 0 ? (
                            '—'
                        ) : (
                            <span className="flex flex-col">
                                {rules.map((rule) => (
                                    <span key={rule.name} title={rule.detail}>
                                        {rule.count > 1
                                            ? `${rule.name} ×${String(rule.count)}`
                                            : rule.name}
                                    </span>
                                ))}
                            </span>
                        )
                    }
                />
            </TabSection>
            <TabSection title={t('comision.corteTitulo', { defaultValue: 'Corte' })}>
                <InfoRow
                    label={t('comision.corte', { defaultValue: 'Corte' })}
                    value={weekLabel(accrual.periodId)}
                />
                <InfoRow
                    label={t('comision.estadoCorte', { defaultValue: 'Estado corte' })}
                    value={
                        <Badge variant={cut.variant} className="px-2 py-0 text-[10px]">
                            {cut.label}
                        </Badge>
                    }
                />
                <PendingDetailRow
                    label={t('comision.pagoProgramado', { defaultValue: 'Pago programado' })}
                    reason={PAYMENT_DATE_NO_SOURCE_NOTE}
                />
            </TabSection>
            {reversed && (
                <TabNote variant="warn">
                    {t('comision.revertida', {
                        defaultValue:
                            'La venta se canceló: esta comisión se revirtió y no se paga.',
                    })}
                </TabNote>
            )}
            {accrual.status === CommissionAccrualStatusEnum.ACCRUED && (
                <TabNote>
                    {t('comision.provisional', {
                        defaultValue:
                            'El monto es el bono por venta. Al cerrar el corte se suman el multiplicador y los umbrales del vendedor.',
                    })}
                </TabNote>
            )}
        </div>
    );
}
